import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { JobComposer } from '../../components/admin/JobComposer';
import { JobFormProvider, useJobForm } from '../../contexts/JobFormContext';
import { unifiedApiService } from '../../services/unified';

// Wrapper component to provide context
export function AdminJobCreatePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const jobIdFromQuery = searchParams.get('jobId');
  const jobId = id || jobIdFromQuery || undefined;

  return (
    <JobFormProvider jobId={jobId}>
      <AdminJobCreatePageContent jobId={jobId} />
    </JobFormProvider>
  );
}

// Main content component
function AdminJobCreatePageContent({ jobId }: { jobId?: string }) {
  const navigate = useNavigate();
  const isEditing = Boolean(jobId);

  const {
    formData,
    loadFormData,
    saveAsDraft,
    publishJob,
    isSaving,
    isPublishing,
  } = useJobForm();

  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  // Load existing job data if editing
  useEffect(() => {
    if (isEditing && jobId && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      
      const fetchJob = async () => {
        try {
          const [job, companies] = await Promise.all([
            unifiedApiService.jobs.getAdminJobById(jobId),
            unifiedApiService.companies.getCompanies()
          ]);
          
          // Find company_id from company name if not provided
          let companyId = (job as any).company_id;
          if (!companyId && job.company) {
            const matchingCompany = companies.find(c => c.name === job.company);
            companyId = matchingCompany?.id;
          }
          
          // Use loadFormData instead of updateMultipleFields to avoid marking as dirty
          loadFormData({
            title: job.title || '',
            description: job.description || '',
            location: job.location || '',
            company_name: job.company || '',
            company_id: companyId,
            company_logo_url: job.company_logo_url,
            job_image_url: job.job_image_url,
            salary_min: job.salary_min,
            salary_max: job.salary_max,
            job_type: job.job_type,
            experience_level: job.experience_level,
            requirements: job.requirements || [],
            benefits: job.benefits || [],
            status: (job.status as any) || 'draft',
            is_featured: (job as any).is_featured || false,
            educational_qualifications: (job as any).educational_qualifications || [],
            template_id: (job as any).template_id,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch job');
          hasLoadedRef.current = false; // Reset on error so it can retry
        }
      };

      fetchJob();
    }
  }, [isEditing, jobId, loadFormData]);

  // Handle save draft
  const handleSaveDraft = async () => {
    try {
      const savedJob = await saveAsDraft();
      if (savedJob && !jobId) {
        navigate(`/admin/jobs/create?jobId=${savedJob.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    }
  };

  // Handle publish
  const handlePublish = async () => {
    try {
      const publishedJob = await publishJob();
      if (publishedJob) {
        navigate('/admin/jobs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish job');
    }
  };

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Job' : 'Create New Job'}
          </h1>

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        {/* JobComposer Component */}
        <div className="flex-1 overflow-hidden">
          <JobComposer
            jobId={jobId}
            onSave={handleSaveDraft}
            onPublish={handlePublish}
            onCancel={() => navigate('/admin/jobs')}
          />
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            onClick={() => navigate('/admin/jobs')}
            className="order-last sm:order-first w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
          >
            Cancel
          </button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-3">
            <button
              onClick={handleSaveDraft}
              disabled={isSaving || isPublishing}
              className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isSaving ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              onClick={handlePublish}
              disabled={isSaving || isPublishing || formData.completeness_percentage < 100}
              className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              title={formData.completeness_percentage < 100 ? 'Complete all required fields to publish' : ''}
            >
              {isPublishing ? 'Publishing...' : 'Publish Job'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
